import { useEffect, useMemo, useState } from "react";
import {
  Bot,
  Download,
  FileClock,
  Fingerprint,
  Search,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";
import { listAuditLogs } from "../api";
import type { AuditLogEntry } from "../types";

function csvCell(value: unknown) {
  const rendered = typeof value === "string" ? value : JSON.stringify(value);
  return `"${rendered.replaceAll('"', '""')}"`;
}

export function AuditPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<AuditLogEntry[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    listAuditLogs()
      .then((items) => {
        setRows(items);
        setSelectedId((current) =>
          items.some((item) => item.id === current) ? current : items[0]?.id ?? null,
        );
        setError("");
      })
      .catch((cause) =>
        setError(cause instanceof Error ? cause.message : "No fue posible cargar la auditoría"),
      );
  }, [open]);

  const visibleRows = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase("es-MX");
    if (!normalized) return rows;
    return rows.filter((row) =>
      [
        row.actor_name,
        row.actor_email,
        row.action,
        row.resource_type,
        row.resource_id,
        JSON.stringify(row.details),
      ]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase("es-MX").includes(normalized)),
    );
  }, [query, rows]);
  const selected =
    visibleRows.find((row) => row.id === selectedId) || visibleRows[0] || rows[0];
  const summary = useMemo(
    () => ({
      total: rows.length,
      system: rows.filter((row) => row.user_id === null).length,
      actors: new Set(rows.filter((row) => row.user_id).map((row) => row.user_id)).size,
    }),
    [rows],
  );

  function exportCsv() {
    const header = ["id", "fecha", "actor", "correo", "accion", "recurso", "resource_id", "detalle"];
    const lines = visibleRows.map((row) =>
      [
        row.id,
        row.created_at,
        row.actor_name,
        row.actor_email || "",
        row.action,
        row.resource_type,
        row.resource_id || "",
        row.details,
      ]
        .map(csvCell)
        .join(","),
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `gsf-auditoria-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel audit-panel" role="dialog" aria-modal="true" aria-labelledby="audit-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">TRAZABILIDAD · ACCIONES HUMANAS Y AUTOMÁTICAS</span>
            <h2 id="audit-title">Auditoría del sistema</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar auditoría"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><FileClock size={17} /><b>{summary.total}</b> registros</span>
          <span><Bot size={17} /><b>{summary.system}</b> automáticos</span>
          <span><UserRound size={17} /><b>{summary.actors}</b> actores</span>
        </section>
        <div className="audit-panel__body">
          <div className="audit-toolbar">
            <label><Search size={14} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Usuario, acción, recurso o detalle..." /></label>
            <b>{visibleRows.length} coincidencias</b>
            <button type="button" onClick={exportCsv} disabled={!visibleRows.length}><Download size={13} /> EXPORTAR CSV</button>
          </div>
          <div className="audit-content">
            <section className="audit-list">
              {visibleRows.map((row) => (
                <button type="button" className={`${selected?.id === row.id ? "is-selected" : ""}${row.user_id ? "" : " is-system"}`} onClick={() => setSelectedId(row.id)} key={row.id}>
                  <span>{row.user_id ? <UserRound size={15} /> : <Bot size={15} />}</span>
                  <span><small>{row.actor_name} · {new Date(row.created_at).toLocaleString("es-MX")}</small><strong>{row.action}</strong><em>{row.resource_type} · {row.resource_id || "sin ID"}</em></span>
                  <b>#{row.id}</b>
                </button>
              ))}
              {!visibleRows.length && <p className="maintenance-empty">SIN REGISTROS COINCIDENTES</p>}
            </section>
            <section className="audit-detail">
              {selected && (
                <>
                  <div className="audit-fingerprint"><Fingerprint size={34} /><span><small>REGISTRO INMUTABLE</small><b>#{selected.id}</b></span></div>
                  <div className="audit-detail__title"><span className="eyebrow">{selected.resource_type} · {selected.resource_id || "SIN ID"}</span><h3>{selected.action}</h3><p>{new Date(selected.created_at).toLocaleString("es-MX")}</p></div>
                  <div className="audit-actor"><ShieldCheck size={18} /><span><small>ACTOR REGISTRADO</small><strong>{selected.actor_name}</strong><em>{selected.actor_email || "Proceso automático del sistema"}</em></span></div>
                  <div className="audit-json">
                    <span className="eyebrow">DETALLE ESTRUCTURADO</span>
                    <pre>{JSON.stringify(selected.details, null, 2)}</pre>
                  </div>
                </>
              )}
            </section>
          </div>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
